import React from "react";
import {
  Box,
  Card,
  Flex,
  Group,
  Text,
  Badge,
  ActionIcon,
  Button,
  Avatar,
  Tooltip,
} from "@mantine/core";
import { IconBallBasketball, IconSearch, IconTrash, IconX } from "@tabler/icons-react";
import { StackedQuery } from "../../models/QueryStack";
import { getPlayerHeadshotUrl } from "../../utils/player";
import { nbaTeamsMap, wnbaTeamsMap } from "../../models/NBA";
import { getGameString } from "../../utils/utils";

export interface QueryStackProps {
  queries: StackedQuery[];
  selectedQueryId?: string | null;
  onSelectQuery?: (query: StackedQuery) => void;
  onRemove: (queryId: string) => void;
  onClearAll: () => void;
  onExecute: () => void;
  isLoading?: boolean;
}

export const QueryStack: React.FC<QueryStackProps> = ({
  queries,
  selectedQueryId,
  onSelectQuery,
  onRemove,
  onClearAll,
  onExecute,
  isLoading = false,
}) => {
  if (queries.length === 0) return null;

  return (
    <Box mb="md" px="xs">
      <Flex justify="space-between" align="center" mb={6}>
        <Group gap={6}>
          <Text fw={700} fz="xs" c="dimmed">
            STACKED QUERIES
          </Text>
          <Badge size="xs" variant="filled" color="accent" circle>
            {queries.length}
          </Badge>
        </Group>
        {queries.length > 1 && (
          <Button
            variant="subtle"
            color="gray"
            size="compact-xs"
            leftSection={<IconTrash size={12} />}
            onClick={onClearAll}
          >
            Clear all
          </Button>
        )}
      </Flex>

      <Flex direction="column" gap="xs">
        {queries.map((q) => {
          const isPlayer = q.type === "player";
          const headshotUrl = isPlayer
            ? getPlayerHeadshotUrl(q.player.id, q.league)
            : undefined;

          const teamName =
            isPlayer && q.oppTeamId !== "0"
              ? nbaTeamsMap[q.oppTeamId]?.nickname || wnbaTeamsMap[q.oppTeamId]?.nickname
              : "";

          const isSelected = selectedQueryId === q.id;

          return (
            <Card
              key={q.id}
              p={8}
              radius="md"
              style={{
                backgroundColor: isSelected
                  ? "var(--mantine-color-accent-light)"
                  : "var(--mantine-color-dark-6)",
                border: isSelected
                  ? "1px solid var(--mantine-color-accent-filled)"
                  : "1px solid var(--mantine-color-dark-4)",
                cursor: onSelectQuery ? "pointer" : "default",
                transition: "all 150ms ease",
              }}
              onClick={() => onSelectQuery?.(q)}
            >
              <Flex justify="space-between" align="center" gap="xs">
                <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  {isPlayer ? (
                    <Avatar
                      src={headshotUrl}
                      alt={q.player.fullName}
                      size="sm"
                      radius="xl"
                      color="accent"
                    >
                      {q.player.fullName.slice(0, 2).toUpperCase()}
                    </Avatar>
                  ) : (
                    <Avatar size="sm" radius="xl" color="accent">
                      <IconBallBasketball size={16} />
                    </Avatar>
                  )}

                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} align="center" wrap="nowrap">
                      <Text fz="xs" fw={600} truncate>
                        {isPlayer ? q.player.fullName : getGameString(q.game)}
                      </Text>
                      {isSelected && (
                        <Badge size="xs" variant="filled" color="accent" radius="sm">
                          Editing
                        </Badge>
                      )}
                    </Group>

                    <Group gap={4} mt={2} wrap="wrap">
                      {q.categories.map((s) => (
                        <Badge key={s} size="xs" variant="light" color="accent" radius="sm">
                          {s}
                        </Badge>
                      ))}

                      {isPlayer &&
                        q.seasons.map((season) => (
                          <Badge key={season} size="xs" variant="outline" color="gray" radius="sm">
                            {season}
                          </Badge>
                        ))}

                      {!isPlayer && q.singleSeason && (
                        <Badge size="xs" variant="outline" color="gray" radius="sm">
                          {q.singleSeason}
                        </Badge>
                      )}

                      {teamName && (
                        <Badge size="xs" variant="light" color="teal" radius="sm">
                          vs {teamName}
                        </Badge>
                      )}

                      {isPlayer && q.lastNGames && q.lastNGames !== "0" && (
                        <Badge size="xs" variant="outline" color="yellow" radius="sm">
                          Last {q.lastNGames}
                        </Badge>
                      )}

                      {isPlayer && (q.dateFrom || q.dateTo) && (
                        <Badge size="xs" variant="outline" color="violet" radius="sm">
                          {q.dateFrom || "Start"} to {q.dateTo || "End"}
                        </Badge>
                      )}

                      {q.periods && q.periods.length > 0 && (
                        <Badge size="xs" variant="outline" color="indigo" radius="sm">
                          {[...q.periods].sort((a, b) => a - b).map((p) => (p <= 4 ? `Q${p}` : "OT")).join(", ")}
                        </Badge>
                      )}
                    </Group>
                  </Box>
                </Group>

                <Tooltip label="Remove from stack" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(q.id);
                    }}
                    aria-label="Remove query"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            </Card>
          );
        })}
      </Flex>

      <Button
        mt="xs"
        w="100%"
        size="xs"
        radius="md"
        color="accent"
        leftSection={<IconSearch size={14} />}
        loading={isLoading}
        onClick={onExecute}
      >
        Search Feed ({queries.length} {queries.length === 1 ? "query" : "queries"})
      </Button>
    </Box>
  );
};

export default QueryStack;
